import React, { useEffect, useState } from 'react';
import './WildlifeZoneManager.scss';

interface SpeciesCatalogEntry {
  id: string;
  name: string;
}

interface ZoneSummary {
  id: string;
  name: string;
  species: string[];
  widthUnits: number;
  heightUnits: number;
}

interface ShowEventDetail {
  zones: ZoneSummary[];
  speciesCatalog: SpeciesCatalogEntry[];
}

const send = (payload: Record<string, unknown>): void => {
  if ((window as any).skyrimPlatform?.sendMessage) {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'wildlife-zone-manager', ...payload }));
  }
};

// Manages the non-spatial fields of admin-created wildlife zones (rename,
// eligible species, delete) - mirrors DungeonManager's list+detail structure.
// Zone bounds themselves are captured via "/markzone <id> corner1/corner2"
// in-game (see wildlifeSystem.ts + ZonePreviewOverlay), not from this panel.
const WildlifeZoneManager: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [zones, setZones] = useState<ZoneSummary[]>([]);
  const [speciesCatalog, setSpeciesCatalog] = useState<SpeciesCatalogEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      const list = detail.zones ?? [];
      setZones(list);
      setSpeciesCatalog(detail.speciesCatalog ?? []);
      setVisible(true);
      setSelectedId((prev) => (prev && list.some((z) => z.id === prev) ? prev : list[0]?.id ?? null));
    };
    window.addEventListener('skymp5-client:showWildlifeZoneManager', handler);
    return () => window.removeEventListener('skymp5-client:showWildlifeZoneManager', handler);
  }, []);

  const selected = zones.find((z) => z.id === selectedId) ?? null;

  useEffect(() => {
    if (!selected) return;
    setNameDraft(selected.name);
    setConfirmingDelete(false);
  }, [selectedId, selected?.name]);

  const close = () => {
    setVisible(false);
    send({ action: 'close' });
  };

  const toggleSpecies = (speciesId: string) => {
    if (!selected) return;
    const has = selected.species.includes(speciesId);
    const next = has ? selected.species.filter((s) => s !== speciesId) : [...selected.species, speciesId];
    send({ action: 'setSpecies', zoneId: selected.id, species: next });
  };

  if (!visible) return null;

  return (
    <div className="wildlife-zone-manager">
      <div className="wildlife-zone-manager__window">
        <div className="wildlife-zone-manager__header">
          <span className="wildlife-zone-manager__title">Zones sauvages</span>
          <div className="wildlife-zone-manager__header-buttons">
            <button type="button" className="wildlife-zone-manager__button" onClick={() => send({ action: 'refresh' })}>
              Rafraîchir
            </button>
            <button type="button" className="wildlife-zone-manager__button wildlife-zone-manager__button--secondary" onClick={close}>
              Fermer
            </button>
          </div>
        </div>

        <div className="wildlife-zone-manager__body">
          <div className="wildlife-zone-manager__list">
            {zones.length === 0 && (
              <div className="wildlife-zone-manager__empty">
                Aucune zone créée - utilise "/markzone &lt;id&gt; corner1" puis "corner2" en jeu.
              </div>
            )}
            {zones.map((z) => (
              <button
                type="button"
                key={z.id}
                className={
                  'wildlife-zone-manager__list-item' + (z.id === selectedId ? ' wildlife-zone-manager__list-item--selected' : '')
                }
                onClick={() => setSelectedId(z.id)}
              >
                <span className="wildlife-zone-manager__list-item-name">{z.name}</span>
              </button>
            ))}
          </div>

          <div className="wildlife-zone-manager__detail">
            {!selected && <div className="wildlife-zone-manager__empty">Sélectionne une zone dans la liste.</div>}

            {selected && (
              <>
                <div className="wildlife-zone-manager__section">
                  <label className="wildlife-zone-manager__label">Nom</label>
                  <div className="wildlife-zone-manager__inline">
                    <input
                      className="wildlife-zone-manager__input"
                      type="text"
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                    />
                    <button
                      type="button"
                      className="wildlife-zone-manager__button wildlife-zone-manager__button--small"
                      onClick={() => send({ action: 'rename', zoneId: selected.id, name: nameDraft })}
                    >
                      Renommer
                    </button>
                  </div>
                  <div className="wildlife-zone-manager__meta">
                    Id : {selected.id} · {selected.widthUnits} x {selected.heightUnits} unités (~
                    {Math.round(selected.widthUnits * 0.14)}m x {Math.round(selected.heightUnits * 0.14)}m)
                  </div>
                  <div className="wildlife-zone-manager__meta">
                    Pour revoir le contour en jeu : "/markzone {selected.id} preview" ("preview off" pour l'arrêter).
                  </div>
                </div>

                <div className="wildlife-zone-manager__section">
                  <label className="wildlife-zone-manager__label">Espèces éligibles</label>
                  {speciesCatalog.length === 0 && (
                    <div className="wildlife-zone-manager__empty">
                      Aucune espèce dans le catalogue (data/wildlife.json) - à peupler à la main.
                    </div>
                  )}
                  <div className="wildlife-zone-manager__species-grid">
                    {speciesCatalog.map((s) => (
                      <label className="wildlife-zone-manager__species-item" key={s.id}>
                        <input type="checkbox" checked={selected.species.includes(s.id)} onChange={() => toggleSpecies(s.id)} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="wildlife-zone-manager__section wildlife-zone-manager__section--danger-zone">
                  {!confirmingDelete ? (
                    <button
                      type="button"
                      className="wildlife-zone-manager__button wildlife-zone-manager__button--danger"
                      onClick={() => setConfirmingDelete(true)}
                    >
                      Supprimer cette zone
                    </button>
                  ) : (
                    <div className="wildlife-zone-manager__inline">
                      <span className="wildlife-zone-manager__confirm-text">Supprimer "{selected.name}" définitivement ?</span>
                      <button
                        type="button"
                        className="wildlife-zone-manager__button wildlife-zone-manager__button--danger"
                        onClick={() => send({ action: 'removeZone', zoneId: selected.id })}
                      >
                        Confirmer
                      </button>
                      <button
                        type="button"
                        className="wildlife-zone-manager__button wildlife-zone-manager__button--secondary"
                        onClick={() => setConfirmingDelete(false)}
                      >
                        Annuler
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WildlifeZoneManager;
